import React, { useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Paper,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import axios from 'axios';
import { createApiUrl } from '../Config/apiConfig';

const BetSettingsPage = () => {
  const [formFields, setFormFields] = useState([{ moduleId: '', clientId: '' }]);
  const [response, setResponse] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleAddFields = () => {
    const lastField = formFields[formFields.length - 1];
    if (lastField.moduleId && lastField.clientId) {
      setFormFields([...formFields, { moduleId: '', clientId: '' }]);
    } else {
      setSnackbar({
        open: true,
        message: 'Please fill in the current fields before adding new ones',
        severity: 'warning'
      });
    }
  };

  const handleRemoveFields = (index) => {
    if (formFields.length > 1) {
      const values = [...formFields];
      values.splice(index, 1);
      setFormFields(values);
    }
  };

  const handleInputChange = (index, event) => {
    const values = [...formFields];
    const { name, value } = event.target;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      values[index][name] = value;
      setFormFields(values);
    }
  };

  const handleSubmit = async () => {
    // Validate that all fields are filled
    if (!formFields.every(field => field.moduleId && field.clientId)) {
      setSnackbar({
        open: true,
        message: 'Please fill in all fields',
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(createApiUrl('GAOCasinoSettings'),
        formFields.map(field => ({
          moduleId: field.moduleId,
          clientId: field.clientId
        }))
      );

      setResponse(response.data);

      // Check if any combinations are unsupported
      const unsupportedCombos = response.data.filter(r => !r.isSupported);
      if (unsupportedCombos.length > 0) {
        setSnackbar({
          open: true,
          message: `Some combinations are not supported. Check the results for details.`,
          severity: 'warning'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Settings generated successfully',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setSnackbar({
        open: true,
        message: 'Error generating settings: ' + (error.response?.data || error.message),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    const supportedSettings = response
      .filter(r => r.isSupported)
      .map(r => r.sql)
      .join('\n');

    navigator.clipboard.writeText(supportedSettings).then(
      () => {
        setSnackbar({
          open: true,
          message: 'Copied to clipboard!',
          severity: 'success'
        });
      },
      (err) => {
        console.error('Could not copy text: ', err);
        setSnackbar({
          open: true,
          message: 'Failed to copy to clipboard',
          severity: 'error'
        });
      }
    );
  };

  return (
    <Box sx={{ p: 3, maxWidth: '1200px', margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom>
        Gold Standard Script Generation
      </Typography>

      {formFields.map((field, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            name="moduleId"
            label="Module ID"
            value={field.moduleId}
            onChange={(e) => handleInputChange(index, e)}
            variant="outlined"
            size="small"
            sx={{ width: '200px' }}
          />
          <TextField
            name="clientId"
            label="Client ID"
            value={field.clientId}
            onChange={(e) => handleInputChange(index, e)}
            variant="outlined"
            size="small"
            sx={{ width: '200px' }}
          />
          {formFields.length > 1 && (
            <IconButton
              onClick={() => handleRemoveFields(index)}
              color="error"
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ))}

      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddFields}
        >
          Add More
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Generate Settings'}
        </Button>
      </Box>

      {response.length > 0 && (
        <>
          {/* Display unsupported combinations first */}
          {response.filter(r => !r.isSupported).map((result, index) => (
            <Alert severity="warning" sx={{ mb: 2 }} key={`unsupported-${index}`}>
              {result.message}
            </Alert>
          ))}

          {/* Display supported settings */}
          {/* Display supported settings */}
          {response.filter(r => r.isSupported).length > 0 && (
            <Paper sx={{ p: 2, mt: 3, position: 'relative' }}>
              <Box sx={{ position: 'absolute', right: 8, top: 8 }}>
                <IconButton onClick={copyToClipboard} title="Copy to clipboard">
                  <ContentCopyIcon />
                </IconButton>
              </Box>
              <Typography variant="h6" gutterBottom>
                Generated Settings
              </Typography>
              <pre className="generated-sql" style={{
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                maxHeight: '500px',
                overflow: 'auto',
                padding: '16px',
                borderRadius: '4px',
                marginTop: '8px',
                backgroundColor: 'var(--background-secondary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)'
              }}>
                {response.filter(r => r.isSupported).map(r => r.sql).join('\n')}
              </pre>
            </Paper>
          )}
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BetSettingsPage;